import React, { useEffect, useMemo, useRef, useState } from "react";
import Footer from "components/layout/footer";
import Skeleton from "react-loading-skeleton";
import expiryDateValidation, {
  checkBankManualFormOrPlaid,
  checkNewPaymentDetails,
  checkOldPaymentDetails,
  formatCurrency,
  formatExpirationDate,
  getBankName,
  getCardType,
  getGeneralDetailMarkers,
  getOperatingSystem,
  validateCard,
  validateEmail,
  decode,
  checkPhone,
  formatFrequency,
  calculateUpsellSavings,
  getMethodCode,
  clearSlice,
  encode,
} from "utils/HelperFunctions";
import logo from "icons/smallLogo.svg";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goToInvalid } from "utils/invalidRoute";
import {
  onSetFinalDisclosureLoading,
  onSetLastRoute,
  onSetShowDisclosure,
  useIsLogin,
  useIsSmallSidebar,
  useShowFinalDisclosure,
} from "store/slices/commonSlice";
import {
  updateTokenExpiredStatus,
  updateTokenStatus,
  useUserData,
} from "store/slices/authSlice";
import { useProductLinkData } from "hooks/useProductLinkData";
import { usePaymentErrors } from "hooks/usePaymentErrors";
import { useCardState } from "hooks/useCardState";
import { useBankState } from "hooks/useBankState";
import { usePaymentProcess } from "hooks/usePaymentProcess";
import { useDefaultPaymentMethodView } from "hooks/useDefaultPaymentMethodView";
import InputField from "components/form/InputField";
import Tag from "icons/subscribe/Tag";
import { CloseIcon } from "icons/close/CloseIcon";
import { AddCircleIconBlue } from "utils/icon";
import PaymentProcessStep2 from "pages/payments/PaymentProcessStep2";
import PaymentMethodsComponent from "components/paymentMethod/PaymentMethodsComponent";
import { CardIcon } from "icons/payments/Card";
import { BankIconSm } from "icons/payments/BankIconSm";
import Button from "components/form/Button";
import moment from "moment";
import WireTransferIcon from "icons/payments/WireTransferIcon";
import { PaypalIcon } from "icons/dashboard/paypal";
import { CashAppIcon } from "icons/dashboard/cashAppIcon";
import ProductLinkSuccess from "components/makePayment/ProductLinkSuccess";
import PaymentLinkProcessFailed from "components/actionCenter/PaymentLinkProcessFailed";
import { productLinkGet } from "store/slices/actionSlice";
import {
  decodeAndValidatePublicLink,
  isInvalidPublicLinkResult,
} from "utils/publicLinkValidation";
import WirePaymentSuccess from "components/actionCenter/WirePaymentSuccess";
import {
  useLeadData,
  usePortalSettingsData,
} from "store/slices/dashboardSlice";
import {
  convFeeGet,
  createPaypalPaymentToken,
  createPaypalToken,
  paymentDownload,
  productLinkPay,
  updateCompanyDetails,
  storePayPalOrder,
  updateLogoAndFavicon,
  updatePaymentPermission,
  wirePaymentProcess,
  resetDuplicateOrder,
  resetCheckDuplicatePayment,
  setIsPaymentProcessing,
  verifyPaymentsViaStax,
} from "store/slices/paymentsSlice";
import {
  oldPaymentMethodsGet,
  useBankData,
  useCardData,
  useHasLoadedPaymentMethods,
  useIsDataLoading,
  useOldWalletData,
} from "store/slices/paymentMethodSlice";
import DisclosureInitial from "components/makePayment/DisclosureInitial";
import DisclosureFinal from "components/makePayment/DisclosureFinal";
import AmazonPay from "icons/dashboard/AmazonPay";
import { FormRef, PaymentGateway, SettingsValue } from "types/payments";
import { PaymentMethodList } from "types/payments";
import { RootState } from "store/store";
import BigInfoCircle from "icons/subscription/BigInfoCircle";
import { defaultPaymentMethodList } from "constants/paymentMethod";
import ToggleSwitch from "components/form/ToggleSwitch";
import {
  checkAccountSubscription,
  invoiceDetailsSend,
  resetSubscriptionState,
} from "store/slices/subscriptionSlice";
import {
  VerifyStaxCardPaymentParams,
  VerifyStripeCardPaymentParams,
} from "types/agreement";
import NotAllowed from "components/subscriptions/NotAllowed";
import { TOKEN_STORAGE_KEY, TOKEN_STORAGE_TIME } from "store/api/axiosApi";
import { TestIdScope, useTestAttrsFactory, useTid } from "utils/testid";

const ProductLinkContent = () => {
  const dispatch = useAppDispatch();
  const testAttrs = useTestAttrsFactory();
  const accountLeadData = useLeadData();
  const userData = useUserData();
  const location = useLocation();
  const walletList = useOldWalletData();
  const searchParams = new URLSearchParams(location.search);
  const drawerRef = useRef<HTMLSpanElement>(null);
  const navigate = useNavigate();
  const isSmallSidebar = useIsSmallSidebar();
  const portalSettings = usePortalSettingsData();
  const { financialSettings } = useAppSelector(
    (state: RootState) => state.auth
  );

  // Extracted state hooks
  const productLinkData = useProductLinkData();
  const paymentErrors = usePaymentErrors();
  const cardState = useCardState();
  const bankState = useBankState();
  const paymentProcess = usePaymentProcess();
  const setPaymentProcessIsAddNewCard = paymentProcess.setIsAddNewCard;

  // Regular state declarations
  const [initialDisclosureAccept, setInitialDisclosureAccept] =
    useState<boolean>(false);
  const { isSubscriptionAllowed } = useAppSelector(
    (state: RootState) => state.subscription
  );
  const [showDetails, setShowDetails] = useState<boolean>(true);
  const [addPromo, setAddPromo] = useState<boolean>(false);
  const [promoEdit, setPromoEdit] = useState<string>("");
  const [selectedState, setSelectedState] = useState<any>(undefined);
  const [isRingLoading, setIsRingLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);

  const [isGoogleLoading, setIsGoogleLoading] = useState<boolean>(false);
  const [isStaxPayPalLoading, setIsStaxPayPalLoading] =
    useState<boolean>(false);
  const [showButtons, setShowButtons] = useState<boolean>(true);
  const [savedCardsEmail, setSavedCardsEmail] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState<boolean>(false);
  const [isManualForm, setIsManualForm] = useState<boolean>(true);
  const [paymentMethodList, setPaymentMethodList] = useState<
    PaymentMethodList[]
  >(defaultPaymentMethodList);
  const [paymentLogo, setPaymentLogo] = useState<any>(logo);
  const [selectedMethod, setSelectedMethod] = useState<
    "Card" | "Bank" | "PayPal" | "Cash App Pay" | string
  >("Card");
  const [tokenData, setTokenData] = useState<any>(null);
  const [amount, setAmount] = useState<any>(0);
  const [isAddNewCard, setIsAddNewCard] = useState<boolean>(false);
  const [isBTNLoading, setIsBTNLoading] = useState<boolean>(false);
  const [isScreenLoading, setIsScreenLoading] = useState<boolean>(false);

  const isDataLoading = useIsDataLoading();
  const hasLoadedPaymentMethods = useHasLoadedPaymentMethods();
  const { brandColor, isDark } = useAppSelector(
    (state: RootState) => state.dashboard
  );
  const cashAppRef = useRef<FormRef>(null);
  const bankRef = useRef<FormRef>(null);
  const cardRef = useRef<FormRef>(null);
  const amazonRef = useRef<FormRef>(null);
  const showFinalDisclosure = useShowFinalDisclosure();
  const isLogin = useIsLogin();

  const initialState = {
    filterParamValue: "",
    filterParam: "search",
    page: 1,
    state: "AL",
    country: "US",
    isrecurring: "N",
    agreementType: "new",
    type: "Amount",
    amount: "0.00",
    new_offer_type: "Amount",
    paymentPlan: { start_date: moment().format("MM/DD/YYYY") },
    selectedCard: {},
    selectedBank: {},
  };
  const cardList = useCardData();
  const bankList = useBankData();
  const promoRef = useRef(null);
  const stepperAnchorRef = useRef<HTMLDivElement>(null);
  const [dataPayment, setDataPayment] = useState<any>(initialState);

  const { paymentPermission, conFee, payPalOrder, companyDetails } =
    useAppSelector((state: RootState) => state.payment);
  const { id: productId } = useParams();

  const getTitle = async (productLinkData: any, companyLogoDetails: any) => {
    let oneTimeAmount = 0,
      recurrAmount = 0,
      frequency = "";

    productLinkData?.productLinksPrices?.forEach((item: any) => {
      if (item?.priceDetails?.frequency === "recurring") {
        setIsRecurring(true);
        frequency = formatFrequency(
          item?.priceDetails?.schedule,
          item?.priceDetails?.interval_custom_count,
          item?.priceDetails?.interval_custom_selector
        );
        recurrAmount =
          recurrAmount + item?.priceDetails?.price * item?.quantity;
      } else {
        oneTimeAmount =
          oneTimeAmount + item?.priceDetails?.price * item?.quantity;
      }
    });
    let title = productLinkData?.productLinksPrices?.[0]?.productDetails?.name;
    setAmount(oneTimeAmount + recurrAmount);
    if (oneTimeAmount === 0 || recurrAmount === 0) {
      if (oneTimeAmount === 0) {
        return {
          title: `Subscribe to ${title} ${
            productLinkData?.productLinksPrices?.length > 1
              ? `and ${productLinkData?.productLinksPrices?.length - 1} more`
              : ""
          }`,
          amount: recurrAmount,
          subtitle: `${frequency}`,
        };
      } else {
        return {
          title:
            productLinkData?.productLinksPrices?.length === 1
              ? title
              : `${title} ${
                  productLinkData?.productLinksPrices?.length > 1
                    ? `and ${
                        productLinkData?.productLinksPrices?.length - 1
                      } more`
                    : ""
                }`,
          amount: oneTimeAmount,
          subtitle: ``,
        };
      }
    } else {
      return {
        title: `Pay ${companyLogoDetails?.name}`,
        amount: oneTimeAmount + recurrAmount,
        subtitle: `then ${formatCurrency(recurrAmount)} ${frequency}`,
      };
    }
  };

  const getProductDetails = async () => {
    const decodedLink = decodeAndValidatePublicLink(productId);
    if (!decodedLink) {
      goToInvalid(navigate, "link");
      return;
    }

    try {
      let payload = {
        slug: productId,
        clusterid: decodedLink.clusterId,
        lead_id: accountLeadData?.lead_id || "",
      };
      let res: any = await dispatch(productLinkGet(payload)).unwrap();

      if (isInvalidPublicLinkResult(res)) {
        goToInvalid(navigate, "link");
        return;
      }

      if (res?.body?.success) {
        let { data } = res?.body;
        const {
          productLinkData: prodData,
          paymentPermission,
          companyLogoDetails,
        } = data;

        if (prodData?.pl_status !== "active") {
          productLinkData.setIsProdLinkActive(false);
        }

        productLinkData.setUpsellPriceData(data?.upsellPrice);
        productLinkData.setProductLinkData(data);

        productLinkData.setProdPermission({
          collect_address: prodData?.collect_address,
          collect_phone: prodData?.collect_phone,
          custom_conv: prodData?.custom_conv || "N",
        });
        dispatch(updatePaymentPermission(paymentPermission));
        dispatch(updateLogoAndFavicon(companyLogoDetails));
        dispatch(
          updateCompanyDetails({ ...companyDetails, ...companyLogoDetails })
        );
        setPaymentLogo(companyLogoDetails?.logo);
        productLinkData.setProductData(prodData?.productLinksPrices);
        let { title, amount, subtitle } = await getTitle(
          prodData,
          companyLogoDetails
        );
        productLinkData.setTitle(title);
        productLinkData.setEditVal(amount);
        productLinkData.setSubtitle(subtitle);
      }
    } catch (error) {
      if (isInvalidPublicLinkResult(error)) {
        goToInvalid(navigate, "link");
        return;
      }
      console.log("error =====", error);
    } finally {
      productLinkData.setIsLoading(false);
    }
  };

  useEffect(() => {
    getProductDetails();
  }, []);

  const onOldCardDataGet = async () => {
    try {
      let productSlug = decode(productId || "")?.split("_");
      let payload = {
        accountid: userData?.data?.accountData?.accountid,
        accounts_id: userData?.data?.accountData?.id,
        clusterid: productSlug[1],
      };
      await dispatch(oldPaymentMethodsGet(payload)).unwrap();
    } catch (error: any) {
      console.log("old card data get erro:-->", error?.message);
    }
  };

  useEffect(() => {
    if (isLogin) {
      onOldCardDataGet();
    }
  }, [isLogin]);

  const scrollToStepper = () => {
    if (stepperAnchorRef.current) {
      stepperAnchorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    if (showFinalDisclosure) {
      requestAnimationFrame(() => {
        scrollToStepper();
      });
    }
  }, [showFinalDisclosure]);

  const usableSavedPaymentMethods = useDefaultPaymentMethodView({
    cardList,
    bankList,
    walletList,
    paymentMethodList,
    paymentPermission,
    isLogin,
    hasLoadedPaymentMethods,
    setShowSavedMethods: setIsAddNewCard,
    setDataPayment,
    selectedMethod,
    setSelectedMethod,
  });

  useEffect(() => {
    setPaymentProcessIsAddNewCard(
      isAddNewCard && usableSavedPaymentMethods.hasUsableSavedMethods
    );
  }, [
    isAddNewCard,
    setPaymentProcessIsAddNewCard,
    usableSavedPaymentMethods.hasUsableSavedMethods,
  ]);

  // payment permission
  useEffect(() => {
    if (productLinkData.productLinkData) {
      const { paymentPermission, productLinkData: prodData } =
        productLinkData.productLinkData || {};
      if (paymentPermission) {
        let methodList = [];

        const isRecurring = prodData?.productLinksPrices?.some(
          (item: any) => item?.priceDetails?.frequency === "recurring"
        );

        const isPayPalSupported =
          !isRecurring ||
          paymentPermission?.pp_gateway_name?.name !== PaymentGateway.STAX;

        if (paymentPermission.cc_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Card",
            icon: <CardIcon />,
          });
        }

        if (paymentPermission.ach_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Bank",
            icon: <BankIconSm />,
          });
        }

        if (paymentPermission.capp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Cash App Pay",
            icon: <CashAppIcon />,
          });
        }
        if (
          !isRecurring &&
          paymentPermission.wt_ext_availability === SettingsValue.YES
        ) {
          methodList.push({
            name: "Wire",
            icon: <WireTransferIcon />,
          });
        }
        if (paymentPermission.amzp_ext_availability === SettingsValue.YES) {
          methodList.push({
            name: "Amazon Pay",
            icon: <AmazonPay />,
          });
        }
        if (
          paymentPermission.pp_ext_availability === SettingsValue.YES &&
          isPayPalSupported
        ) {
          methodList.push({
            name: "PayPal",
            icon: <PaypalIcon />,
          });
        }
        if (paymentPermission.cc_ext_availability === SettingsValue.NO) {
          setSelectedMethod(methodList?.[0]?.name);
        }

        setPaymentMethodList(methodList);
      } else {
        setPaymentMethodList(defaultPaymentMethodList);
      }
    }
  }, [productLinkData.productLinkData]);

  const handleCheckAccountSubscription = async (payload: any) => {
    try {
      await dispatch(checkAccountSubscription(payload)).unwrap();
    } catch (error) {
      console.log("handleCheckAccountSubscription error ======", error);
    }
  };

  useEffect(() => {
    if (
      productLinkData.productLinkData &&
      portalSettings &&
      isLogin &&
      financialSettings?.subscriptions_limit === 1
    ) {
      const { productLinkData: prodData } =
        productLinkData.productLinkData || {};

      const isRecurring = prodData?.productLinksPrices?.some(
        (item: any) => item?.priceDetails?.frequency === "recurring"
      );

      if (isRecurring) {
        let productSlug = decode(productId || "")?.split("_");
        let finEmail = !isAddNewCard ? paymentProcess.email : savedCardsEmail;

        if (finEmail && validateEmail(finEmail)) {
          let payload: any = {
            email: finEmail,
            clusterid: productSlug[1],
            accounts_id: userData?.data?.accountData?.id,
          };

          handleCheckAccountSubscription(payload);
        }
      }
    }
  }, [
    productLinkData.productLinkData,
    portalSettings,
    paymentProcess.email,
    savedCardsEmail,
    isAddNewCard,
    isLogin,
  ]);

  const handleEmailBlur = () => {
    let email = !isAddNewCard ? paymentProcess.email : savedCardsEmail;
    if (validateEmail(email)) {
      let productSlug = decode(productId || "")?.split("_");

      let payload: any = {
        email: email,
        clusterid: productSlug[1],
      };

      handleCheckAccountSubscription(payload);
    }
  };

  //bank name get
  const findBankByRoutingNumber = async (routingNumber: string) => {
    setIsRingLoading(true);
    try {
      let bankObj = await getBankName(routingNumber);

      if (bankObj?.type === "success") {
        bankState.setBankName(bankObj?.bank_name);
        bankState.setBank(bankObj?.bank_name);
      } else {
        paymentErrors.setErrMsg("Invalid routing number");
        setIsRingLoading(false);
      }
    } catch (error) {
      console.log("error =====", error);
      paymentErrors.setErrMsg("Invalid routing number");
      setIsRingLoading(false);
    } finally {
      setIsRingLoading(false);
    }
  };

  // card number
  const handleChangeCard = (e: any, name: string) => {
    let value = { ...cardState.values };
    if (name === "x_card_num") {
      let icon = getCardType(e.target.value) || cardState.iconType;
      if (icon === "amex") {
        if (e.target.value.length < 18) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(\d{6})(\d{5})/, "$1 $2 $3");
          cardState.setCardNumber(formattedCardNumber);
          value = { ...cardState.values, [name]: e.target.value };
        }
      } else {
        if (e.target.value.length < 20) {
          const cardNumber = e.target.value.replace(/\s/g, "");
          const formattedCardNumber = cardNumber
            .replace(/\D/g, "")
            .replace(/(\d{4})(?=\d)/g, "$1 ");
          cardState.setCardNumber(formattedCardNumber);
          value = { ...cardState.values, [name]: e.target.value };
        }
      }
    } else if (name === "x_card_code") {
      if (cardState.iconType === "amex") {
        if (e.target.value.length < 5) {
          value = { ...cardState.values, [name]: e.target.value };
        }
      } else {
        if (e.target.value.length < 4) {
          value = { ...cardState.values, [name]: e.target.value };
        }
      }
    } else if (name === "x_exp_date") {
      const inputValue = e.target.value;
      cardState.setIsDateErr(false);
      if (inputValue?.length < 6) {
        const isValidDate: any = expiryDateValidation(inputValue);
        const formattedDate = formatExpirationDate(inputValue);
        value = { ...value, [name]: formattedDate };
        !isValidDate?.isValid &&
          inputValue?.length === 5 &&
          cardState.setIsDateErr(true);
      }
    } else if (name === "x_country") {
      value = { ...value, [name]: e?.value };
    } else if (name === "x_state") {
      value = { ...value, [name]: e?.name };
    } else {
      value = { ...value, [name]: e.target.value };
    }

    cardState.setValues(value);
  };

  //change bank
  const handleChangeBank = (e: any, name: string) => {
    if (name === "routing_number") {
      if (e?.target?.value?.length < 10) {
        cardState.setValues({ ...cardState.values, [name]: e.target.value });
        bankState.setBankName("");
        bankState.setBank("");
        paymentErrors.setErrMsg(undefined);
        if (e?.target?.value?.length === 9) {
          findBankByRoutingNumber(e?.target?.value);
        }
      }
    } else if (name === "r_account_number") {
      bankState.setnumberNotmatchedError(false);
      cardState.setValues({ ...cardState.values, [name]: e?.target.value });
      if (
        cardState.values?.account_number?.length === e?.target?.value?.length
      ) {
        bankState.setnumberNotmatchedError(
          cardState.values?.account_number !== e?.target?.value
        );
      }
    } else {
      cardState.setValues({ ...cardState.values, [name]: e?.target?.value });
    }
  };

  //card icon
  useEffect(() => {
    if (!cardState.cardNumber) {
      cardState.setIconType("");
      cardState.setValues({ ...cardState.values, card_type: "" });
      return;
    } else {
      cardState.setIconType(getCardType(cardState.cardNumber));
      cardState.setValues({
        ...cardState.values,
        card_type: getCardType(cardState.cardNumber),
      });
    }
  }, [cardState.cardNumber]);

  // button disable
  useEffect(() => {
    if (isAddNewCard) {
      let response = checkOldPaymentDetails({ dataPayment });
      let checkEmail =
        savedCardsEmail &&
        savedCardsEmail?.trim() === "" &&
        validateEmail(savedCardsEmail);
      const isPhone =
        productLinkData.prodPermission.collect_phone === "Y"
          ? !checkPhone(productLinkData?.phoneNumber)
          : false;

      setIsDisabled(response || checkEmail || isPhone);
    } else {
      let response = checkNewPaymentDetails({
        values: cardState.values,
        cc_billing_address: portalSettings?.cc_billing_address,
        cc_billing_addr_mandatory: portalSettings?.cc_billing_addr_mandatory,
        selectedMethod,
        email: paymentProcess.email,
        isErr: cardState.isErr,
        errMsg: paymentErrors.errMsg,
        collectAddress: productLinkData.prodPermission.collect_address === "Y",
      });

      const isPhone =
        productLinkData.prodPermission.collect_phone === "Y"
          ? !checkPhone(productLinkData?.phoneNumber)
          : false;

      setIsDisabled(response || isPhone);
    }
  }, [
    selectedMethod,
    cardState.values,
    paymentProcess.email,
    isAddNewCard,
    dataPayment,
    cardState.isErr,
    paymentErrors.errMsg,
    productLinkData.prodPermission,
    productLinkData?.phoneNumber,
  ]);

  //back to start on failed payment
  const handleBackToStart = () => {
    cardState.resetCardState();
    paymentErrors.clearErrors();
    paymentProcess.setEmail("");
    paymentProcess.setPaymentProcess(null);
    bankState.resetBankState();
    setInitialDisclosureAccept(false);
    setPaymentLogo(logo);
    dispatch(onSetFinalDisclosureLoading(false));
    dispatch(onSetShowDisclosure(false));
  };

  //wire paymnet
  const paymentViaWire = async () => {
    try {
      let productSlug = decode(productId || "")?.split("_");
      let payload = {
        payment_gateway_id: "",
        amount: parseFloat(amount.toString()).toFixed(2),
        clusterid: productSlug[1],
        x_method: "WTO",
        lead_id: accountLeadData?.lead_id,
        email: userData?.data?.accountData?.email ?? paymentProcess.email,
        accountid: userData?.data?.accountData?.accountid,
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        isProductLink: true,
        ivr_type: "CUST_PRTL",
      };
      let res: any = await dispatch(wirePaymentProcess(payload)).unwrap();
      if (res.body.success) {
        const { data } = res?.body;
        bankState.setWireDetails({
          email: data?.email || "",
          bank: data?.bank_name || "",
          routing: data?.routing_number || "",
          account: data?.account_number || "",
          swift: data?.swift_code || "",
          dueDate:
            moment(
              moment().add(res?.body?.data?.wire_deadline || 0, "days")
            ).format("MM/DD/YYYY") || "",
        });
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setIsWirePayment(true);
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentErrors.setFailedReason(res?.body?.data?.msg);
        paymentProcess.setPaymentProcess("false");
      }
    } catch (error) {
      console.log(" paymentViaWire error=====", error);
    }
  };

  const createPayloadCardBank = async (value: any) => {
    const { lead_owner, balance, first_name, last_name, accountid } =
      userData?.data?.accountData || {};
    const {
      x_payment_method,
      x_city,
      x_address,
      address2,
      x_zip,
      x_state,
      x_country,
      email: emailVal,
      iconType: cardType,
      x_card_num,
      x_card_code,
      x_exp_date,
      choldername,
      routing_number,
      account_number,
      bankName,
      bank,
      cc_token,
      cc_token2,
      stripe_token,
      cppid,
      pc_atid,
      use_old_card,
    } = value;

    let payload = {
      lead_id: accountLeadData?.lead_id,
      cluster_id: decode(productId || "")?.split("_")[1],
      balance: balance,
      x_payment_method: x_payment_method,
      x_email: emailVal,
      city: x_city,
      address1: x_address,
      address2: address2,
      zipcode: x_zip,
      state: x_state,
      country: x_country,
      first_name: first_name,
      last_name: last_name,
      accountid: accountid,
      amount_type: "amount",
      amount: amount,
      use_old_card: use_old_card,
      description: "Online Payment",
      payment_note: !isRecurring ? "One-time" : "Subscription",
      customer_device: getOperatingSystem(),
      card_type: cardType ? cardType : cardState.iconType,
      old_cardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
      old_cvv: x_card_code ? x_card_code : "",
      old_expiry: x_exp_date ? x_exp_date : "",
      old_4digit: x_card_num ? x_card_num?.slice(-4) : "",
      old_type: cardState.iconType ? cardState.iconType : "",
      old_zip: x_zip ? x_zip : "",
      cvv: x_card_code ? x_card_code : "",
      cexpiry: x_exp_date ? x_exp_date : "",
      ccardnumber: x_card_num ? x_card_num?.replaceAll(" ", "") : "",
      choldername: choldername ? choldername : "",
      aholdername: choldername ? choldername : "",
      routing_number: routing_number ? routing_number : "",
      account_number: account_number ? account_number : "",
      bank_name: bankName ? bankName : "",
      bank_address: bank ? bank : "",
      cc_token: cc_token ? cc_token : "",
      cc_token2: cc_token2 || cc_token || "",
      stripe_token: stripe_token ? stripe_token : "",
      product_link: productId,
      agent: lead_owner,
      lead_owner: lead_owner,
      cppid: cppid ?? 0,
      pc_atid: pc_atid ?? 0,
      isUpsellEnabled: productLinkData?.enabled ? "1" : "0",
      custom_conv:
        productLinkData.prodPermission.custom_conv === "Y" ? "on" : "off",
    };
    return payload;
  };

  const handleCardBankPayment = async (values: any) => {
    try {
      let payload = await createPayloadCardBank(values);
      let res: any = await dispatch(productLinkPay(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
      }
      setIsBTNLoading(false);
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed, server error");
      setIsBTNLoading(false);
      console.log("handleCardBankPayment error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  const handleSubmit = async () => {
    try {
      setIsBTNLoading(true);
      if (selectedMethod === "Wire") {
        paymentViaWire();
      } else {
        if (!isAddNewCard) {
          let payload = {
            x_payment_method: getMethodCode(selectedMethod),
            email: paymentProcess.email,
            x_card_num: cardState.values?.x_card_num?.replaceAll(" ", ""),
            x_card_code: cardState.values?.x_card_code,
            x_exp_date: cardState.values?.x_exp_date,
            iconType: cardState.iconType,
            routing_number: cardState.values?.routing_number,
            account_number: cardState.values?.account_number,
            cc_token: "",
            cc_token2: "",
            stripe_token: "",
            bankName: bankState.bankName,
            bank: bankState.bank,
            x_city:
              cardState.values?.x_city ||
              userData?.data?.accountData?.city ||
              "",
            x_address:
              cardState.values?.x_address ||
              userData?.data?.accountData?.address1 ||
              "",
            address2:
              cardState.values?.address2 ||
              userData?.data?.accountData?.address2 ||
              "",
            x_zip:
              cardState.values?.x_zip ||
              userData?.data?.accountData?.postal_code ||
              "",
            x_state:
              cardState.values?.x_state ||
              userData?.data?.accountData?.state ||
              "",
            x_country:
              cardState.values?.x_country ||
              userData?.data?.accountData?.country ||
              "",
            choldername: cardState.values?.choldername,
            cppid: "",
            use_old_card: "0",
          };
          await handleCardBankPayment(payload);
        } else {
          let method = "CC";
          if (
            dataPayment?.selectedBank &&
            Object.keys(dataPayment?.selectedBank).length > 0
          ) {
            method = "ACH";
          }
          let payload = {
            x_payment_method: method,
            email: savedCardsEmail,
            x_card_num:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_4digit
                : dataPayment?.selectedBank?.cc_4digit,
            x_card_code:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_cvv
                : dataPayment?.selectedBank?.cc_cvv,
            x_exp_date:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_expiry
                : dataPayment?.selectedBank?.cc_expiry,
            iconType:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_type
                : dataPayment?.selectedBank?.cc_type,
            routing_number:
              method === "CC"
                ? dataPayment?.selectedCard?.routing_number
                : dataPayment?.selectedBank?.routing_number,
            account_number:
              method === "CC"
                ? dataPayment?.selectedCard?.account_number
                : dataPayment?.selectedBank?.account_number,
            cc_token:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_token
                : dataPayment?.selectedBank?.cc_token,
            cc_token2:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_token2
                : dataPayment?.selectedBank?.cc_token2,
            stripe_token:
              method === "CC"
                ? dataPayment?.selectedCard?.cc_token
                : dataPayment?.selectedBank?.cc_token,
            bankName:
              method === "CC"
                ? dataPayment?.selectedCard?.bank_name
                : dataPayment?.selectedBank?.bank_name,
            x_city:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_city
                : dataPayment?.selectedBank?.billing_city,
            x_address:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_address
                : dataPayment?.selectedBank?.billing_address,
            address2:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_address2
                : dataPayment?.selectedBank?.billing_address2,
            x_zip:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_zip
                : dataPayment?.selectedBank?.billing_zip,
            x_state:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_state
                : dataPayment?.selectedBank?.billing_state,
            x_country:
              method === "CC"
                ? dataPayment?.selectedCard?.billing_country
                : dataPayment?.selectedBank?.billing_country,
            choldername:
              method === "CC"
                ? dataPayment?.selectedCard?.account_name
                : dataPayment?.selectedBank?.account_name,
            cppid:
              method === "CC"
                ? dataPayment?.selectedCard?.id
                : dataPayment?.selectedBank?.id,
            use_old_card: "1",
          };
          await handleCardBankPayment(payload);
        }
      }
    } catch (error) {
      console.log("handleSubmit error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      return true;
    }
  };

  const handleGooglePay = async (value: any, total_amount: any) => {
    try {
      setIsGoogleLoading(true);
      const tokenData = JSON.parse(value?.tokenizationData?.token);
      const { lead_owner, balance, first_name, last_name, accountid } =
        userData?.data?.accountData || {};
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: decode(productId || "")?.split("_")[1],
        balance: balance,
        x_payment_method: "GP",
        x_email: paymentProcess.email,
        city: tokenData?.card?.address_city || "",
        address1: tokenData?.card?.address_line1 || "",
        address2: tokenData?.card?.address_line2 || "",
        zipcode: tokenData?.card?.address_zip || "",
        state: tokenData?.card?.address_state || "",
        country: tokenData?.card?.address_country || "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: tokenData?.card?.brand,
        old_cardnumber: tokenData?.card?.last4,
        old_cvv: "",
        old_expiry: `${tokenData?.card?.exp_month}/${tokenData?.card?.exp_year}`,
        old_4digit: tokenData?.card?.last4 || "",
        old_type: tokenData?.card?.brand || "",
        old_zip: tokenData?.card?.address_zip || "",
        cvv: "",
        cexpiry: `${tokenData?.card?.exp_month}/${tokenData?.card?.exp_year}`,
        ccardnumber: tokenData?.card?.last4 || "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: tokenData?.id,
        stripe_token: tokenData?.id,
        product_link: productId,
        agent: lead_owner,
        lead_owner: lead_owner,
        isUpsellEnabled: productLinkData?.enabled ? "1" : "0",
        custom_conv:
          productLinkData.prodPermission.custom_conv === "Y" ? "on" : "off",
      };
      let res: any = await dispatch(productLinkPay(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
      }
      setIsGoogleLoading(false);
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed, server error");
      setIsGoogleLoading(false);
      console.log("hanleCardBankPayment error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };
  const createPayPalIntent = async () => {
    try {
      const payload = {
        is_recurring: "Y",
        phone_number: userData?.data?.accountData?.phone,
        first_name: userData?.data?.accountData?.first_name,
        last_name: userData?.data?.accountData?.last_name,
        accountid: userData?.data?.accountData?.accountid,
        amount: amount,
        agent: userData?.data?.accountData?.lead_owner,
        lead_owner: userData?.data?.accountData?.lead_owner,
        lead_id: accountLeadData?.lead_id,
        clusterid: decode(productId || "")?.split("_")[1],
        success_url: `${
          window.location.href
        }?method=paypal&amount=${amount}&status=success&isRecurring=${true}&isUpsellEnabled=${productLinkData?.enabled}`,
        cancel_url: `${
          window.location.href
        }?method=paypal&amount=${amount}&status=cancel&isRecurring=${true}&isUpsellEnabled=${productLinkData?.enabled}`,
        start_date: new Date(),
        isProductLink: true,
        email: paymentProcess.email,
      };
      let res: any = await dispatch(createPaypalToken(payload));
      if (res?.payload?.body?.success) {
        await dispatch(
          storePayPalOrder({
            url: res?.payload?.body?.result?.url,
            orderId: res?.payload?.body?.result?.invoice_id,
            token: res?.payload?.body?.result?.token,
            xEmail: paymentProcess.email,
            amount: amount,
          })
        );

        if (
          portalSettings?.payment_agreement_opt === SettingsValue.CUSTOM ||
          portalSettings?.payment_agreement_opt === SettingsValue.DEFAULT
        ) {
          dispatch(onSetShowDisclosure(true));
        } else {
          window.location.href = res?.payload?.body?.result?.url;
        }
      } else {
        paymentErrors.setFailedReason(res?.payload?.body?.data?.msg);
      }
    } catch (error) {
      console.log("createPayPalIntent error =====", error);
    }
  };

  const handleSendEmail = async ({
    email,
    invoice_slug,
    order_id,
    accountId,
  }: any) => {
    try {
      let value = {
        invoice_slug: invoice_slug,
        email: email,
        order_id,
        accountId,
      };
      await sendEmail(value);
    } catch (error) {
      console.error("Failed to send email:", error);
    }
  };

  const verifyCashAppBankPayment = async (
    method: any,
    email: any,
    amount: any
  ) => {
    try {
      productLinkData.setEnabled(
        searchParams.get("isUpsellEnabled") === "true"
      );
      const custom_conv = searchParams.get("custom_conv") || "N";
      const { lead_owner, balance, first_name, last_name, accountid } =
        userData?.data?.accountData || {};
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: decode(productId || "")?.split("_")[1],
        balance: balance,
        x_payment_method: method,
        x_email: email,
        city: "",
        address1: "",
        address2: "",
        zipcode: "",
        state: "",
        country: "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: "",
        old_cardnumber: "",
        old_cvv: "",
        old_expiry: "",
        old_4digit: "",
        old_type: "",
        old_zip: "",
        cvv: "",
        cexpiry: "",
        ccardnumber: "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: "",
        stripe_token: "",
        product_link: productId,
        agent: lead_owner,
        lead_owner: lead_owner,
        transaction_id: searchParams.has("payment_intent")
          ? searchParams.get("payment_intent")
          : searchParams.get("setup_intent"),
        is3ds: method === "CC" ? true : false,
        isUpsellEnabled:
          searchParams.get("isUpsellEnabled") === "true" ? "1" : "0",
        custom_conv: custom_conv === "Y" ? "on" : "off",
      };
      let res: any = await dispatch(productLinkPay(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
    }
  };

  const verifyStripeCardPayment = async ({
    intent,
    email,
    error,
    principalAmount,
    orderId,
  }: VerifyStripeCardPaymentParams) => {
    if (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason(error.message);
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
      // The card form has already marked this order failed; the customer
      // gets the failed-payment email for it.
      if (orderId) {
        await handleSendEmail({
          email: email || userData?.data?.accountData?.email,
          order_id: orderId,
          accountId: userData?.data?.accountData?.accountid,
        });
      }
    } else {
      try {
        const { lead_owner, balance, first_name, last_name, accountid } =
          userData?.data?.accountData || {};
        let payload = {
          lead_id: accountLeadData?.lead_id,
          cluster_id: decode(productId || "")?.split("_")[1],
          balance: balance,
          x_payment_method: "CC",
          x_email: email || userData?.data?.accountData?.email,
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: first_name,
          last_name: last_name,
          accountid: accountid,
          amount_type: "amount",
          amount: principalAmount ?? amount,
          use_old_card: !isAddNewCard ? "0" : "1",
          description: "Online Payment",
          payment_note: !isRecurring ? "One-time" : "Subscription",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          cc_token: "",
          stripe_token: "",
          product_link: productId,
          agent: lead_owner,
          lead_owner: lead_owner,
          transaction_id: intent,
          is3ds: true,
          isUpsellEnabled: productLinkData?.enabled ? "1" : "0",
          custom_conv:
            productLinkData.prodPermission.custom_conv === "Y" ? "on" : "off",
        };
        let res: any = await dispatch(productLinkPay(payload)).unwrap();
        if (res?.body?.success) {
          let { data } = res?.body;
          paymentProcess.setInvoiceNumber(data?.invoice_number || "");
          paymentProcess.setPaymentProcess("true");
          paymentProcess.setInvoiceSlug(data?.invoice_slug);
          paymentProcess.setOrderId(data?.invoice_id);
          paymentProcess.setAmountDisplay(data?.total_amount);
          paymentProcess.setEmail(data?.email);
          paymentProcess.setLeadId(data?.lead_id);
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          paymentProcess.setAccountId(
            data?.accountid || userData?.data?.accountData?.accountid
          );
          await handleSendEmail({
            email: data?.email,
            invoice_slug: data?.invoice_slug,
            order_id: data?.invoice_id,
            accountId:
              data?.accountid || userData?.data?.accountData?.accountid,
          });
        } else {
          paymentProcess.setPaymentProcess("false");
          paymentErrors.setFailedReason(
            res?.body?.message ??
              res?.body?.msg ??
              res?.body?.data?.gateway_error ??
              res?.body?.data?.message ??
              "Payment failed"
          );
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
        }
      } catch (error) {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason("Payment failed");
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        console.log("error =====", error);
      } finally {
        dispatch(resetDuplicateOrder());
        dispatch(resetCheckDuplicatePayment());
        await dispatch(onSetFinalDisclosureLoading(false));
        await dispatch(onSetShowDisclosure(false));
      }
    }
  };

  const verifyStaxCardBank = async ({
    cc_token,
    cc_token2,
    x_method,
  }: VerifyStaxCardPaymentParams) => {
    dispatch(setIsPaymentProcessing(true));

    try {
      const { lead_owner, balance, first_name, last_name, accountid } =
        userData?.data?.accountData || {};
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: decode(productId || "")?.split("_")[1],
        balance: balance,
        x_payment_method: x_method,
        x_email: userData?.data?.accountData?.email,
        city: "",
        address1: "",
        address2: "",
        zipcode: "",
        state: "",
        country: "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: "",
        old_cardnumber: "",
        old_cvv: "",
        old_expiry: "",
        old_4digit: "",
        old_type: "",
        old_zip: "",
        cvv: "",
        cexpiry: "",
        ccardnumber: "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: cc_token,
        cc_token2: cc_token2,
        stripe_token: "",
        product_link: productId,
        agent: lead_owner,
        lead_owner: lead_owner,
        is3ds: false,
        isUpsellEnabled: productLinkData?.enabled ? "1" : "0",
        custom_conv:
          productLinkData.prodPermission.custom_conv === "Y" ? "on" : "off",
      };

      let res: any = await dispatch(productLinkPay(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(
          data?.total_amount || data?.amount || payload?.amount
        );
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const verifyStaxPayPal = async ({
    transactionId,
    error,
  }: {
    transactionId?: string;
    error?: any;
  }) => {
    setIsStaxPayPalLoading(true);
    let slug = decode(productId || "")?.split("_");
    try {
      const { email, first_name, last_name, accountid, phone } =
        userData?.data?.accountData || {};
      let payload = {
        transaction_id: transactionId,
        lead_id: accountLeadData?.lead_id,
        clusterid: slug[1],
        isrecurring: "N",
        x_method: "PP",
        amount: amount,
        accountid: accountid || "",
        first_name: first_name || "",
        last_name: last_name || "",
        phone_number: phone,
        module: "process_payment",
        isProductLink: true,
        productLinkId: slug[0],
        conv_fee: conFee,
        description: "Online Payment",
        payment_note: "One-time",
        isPayForProduct: true,
        custom_conv:
          productLinkData?.prodPermission?.custom_conv === "Y" ? "on" : "off",
        email: email,
      };

      let res: any = await dispatch(verifyPaymentsViaStax(payload)).unwrap();
      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount || data?.amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      setIsStaxPayPalLoading(false);
      dispatch(resetDuplicateOrder());
      dispatch(resetCheckDuplicatePayment());
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const sendEmail = async ({
    invoice_slug,
    accountId,
    email,
    shouldSendAll,
    order_id,
    bank_name,
    routing_number,
    account_number,
    swift_code,
  }: any) => {
    try {
      const clusterId = decode(productId || "")?.split("_")[1];
      const validEmails = (Array.isArray(email) ? email : [email])
        .map((value: any) => (typeof value === "string" ? value.trim() : ""))
        .filter(Boolean);

      if (invoice_slug) {
        if (!accountId || !clusterId || validEmails.length === 0) {
          console.warn("sendEmail skipped: missing required values", {
            invoice_slug,
            accountid: accountId,
            clusterid: clusterId,
            email,
          });
          return false;
        }

        let payload: any = {
          invoice_slug: invoice_slug,
          accountid: accountId,
          clusterid: clusterId,
          email: validEmails[0],
          shouldSendAll: shouldSendAll,
          // Match the automatic receipt path: recurring product links use an
          // invoice receipt, while one-time product links use the product
          // receipt template.
          email_type: isRecurring
            ? "invoice_receipt"
            : "receipt_on_time_products",
        };

        if (bank_name) {
          payload = {
            ...payload,
            bank_name: bank_name,
            routing_number: routing_number,
            account_number: account_number,
            swift_code: swift_code,
          };
        }

        const response: any = await dispatch(
          invoiceDetailsSend(payload)
        ).unwrap();
        return Boolean(response?.body?.success ?? response?.success);
      } else {
        if (!order_id || !accountId || validEmails.length === 0) {
          console.warn("sendEmail skipped: missing required values", {
            invoice_id: order_id,
            accountid: accountId,
            emails: email,
          });
          return false;
        }

        let payload = {
          invoice_id: order_id,
          accountid: accountId,
          emails: validEmails,
          shouldSendAll: shouldSendAll,
        };

        const response: any = await dispatch(paymentDownload(payload)).unwrap();
        return Boolean(response?.body?.success ?? response?.success);
      }
    } catch (error) {
      console.log("sendEmail", error);
      return false;
    }
  };

  const handlePayPalPayment = async (tokendata: any, payPalOrder: any) => {
    try {
      productLinkData.setEnabled(
        searchParams.get("isUpsellEnabled") === "true"
      );
      const custom_conv = searchParams.get("custom_conv") || "N";
      let res: any = await dispatch(
        createPaypalPaymentToken(tokendata)
      ).unwrap();

      if (res?.body?.success) {
        const payload1 = {
          product_link: productId,
          cluster_id: decode(productId || "")?.split("_")[1],
          x_email: payPalOrder.xEmail,
          x_payment_method: "PP",
          cc_token: res?.body?.data?.cc_token,
          cppid: res?.body?.data?.cppid,
          accountid: userData?.data?.accountData?.accountid,
          lead_id: accountLeadData?.lead_id,
          city: "",
          address1: "",
          address2: "",
          zipcode: "",
          state: "",
          country: "",
          first_name: userData?.data?.accountData?.first_name,
          last_name: userData?.data?.accountData?.last_name,
          amount_type: "amount",
          amount: payPalOrder.amount,
          use_old_card: !isAddNewCard ? "0" : "1",
          description: "Online Payment",
          payment_note: !isRecurring ? "One-time" : "Subscription",
          customer_device: getOperatingSystem(),
          card_type: "",
          old_cardnumber: "",
          old_cvv: "",
          old_expiry: "",
          old_4digit: "",
          old_type: "",
          old_zip: "",
          cvv: "",
          cexpiry: "",
          ccardnumber: "",
          choldername: "",
          aholdername: "",
          routing_number: "",
          account_number: "",
          bank_name: "",
          bank_address: "",
          stripe_token: "",
          agent: userData?.data?.accountData?.lead_owner,
          lead_owner: userData?.data?.accountData?.lead_owner,
          transaction_id: searchParams.has("payment_intent")
            ? searchParams.get("payment_intent")
            : searchParams.get("setup_intent"),
          is3ds: false,
          isUpsellEnabled:
            searchParams.get("isUpsellEnabled") === "true" ? "1" : "0",
          custom_conv: custom_conv === "Y" ? "on" : "off",
        };

        let response: any = await dispatch(productLinkPay(payload1)).unwrap();

        if (response?.body?.success) {
          let { data } = response?.body;
          paymentProcess.setInvoiceNumber(data?.invoice_number || "");
          paymentProcess.setPaymentProcess("true");
          paymentProcess.setInvoiceSlug(data?.invoice_slug);
          paymentProcess.setOrderId(data?.invoice_id);
          paymentProcess.setAmountDisplay(data?.total_amount);
          paymentProcess.setEmail(data?.email);
          paymentProcess.setLeadId(data?.lead_id);
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
          paymentProcess.setAccountId(
            data?.accountid || userData?.data?.accountData?.accountid
          );
          handleSendEmail({
            email: data?.email,
            invoice_slug: data?.invoice_slug,
            order_id: data?.invoice_id,
            accountId:
              data?.accountid || userData?.data?.accountData?.accountid,
          });
        } else {
          paymentProcess.setPaymentProcess("false");
          paymentErrors.setFailedReason(
            response?.body?.message ??
              response?.body?.msg ??
              response?.body?.data?.gateway_error ??
              response?.body?.data?.message ??
              "Payment failed"
          );
          paymentProcess.setIsScreenLoading(false);
          setIsScreenLoading(false);
        }
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason("Payment failed");
        paymentProcess.setIsScreenLoading(false);
        setIsScreenLoading(false);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      setIsScreenLoading(false);
      console.log("error =====", error);
    } finally {
      dispatch(onSetFinalDisclosureLoading(false));
      dispatch(onSetShowDisclosure(false));
    }
  };

  // paypal cashapp bank
  useEffect(() => {
    //cash app
    if (
      (searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
      !searchParams.has("checked") &&
      searchParams.has("amount") &&
      searchParams.has("method") &&
      searchParams.get("method") === "cashapp"
    ) {
      setIsScreenLoading(true);
      searchParams.set("checked", "true");
      console.log(
        ' searchParams.get("email") =====',
        searchParams.get("email")
      );
      verifyCashAppBankPayment(
        "CAPP",
        searchParams.get("email"),
        searchParams.get("amount")
      );
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }

    //bank
    if (
      ((searchParams.has("payment_intent") ||
        searchParams.has("setup_intent")) &&
        !searchParams.has("checked") &&
        searchParams.has("amount") &&
        searchParams.has("method") &&
        searchParams.get("method") === "bank") ||
      searchParams.get("method") === "amazonPay"
    ) {
      setIsScreenLoading(true);
      searchParams.set("checked", "true");
      verifyCashAppBankPayment(
        searchParams.get("method") === "bank" ? "ACH" : "AMZP",
        searchParams.get("email"),
        searchParams.get("amount")
      );
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
    if (
      (searchParams.has("approval_session_id") ||
        searchParams.has("approval_token_id")) &&
      !searchParams.has("checked") &&
      searchParams.has("status") &&
      searchParams.has("amount") &&
      searchParams.get("method") === "paypal"
    ) {
      setIsScreenLoading(true);
      searchParams.set("checked", "true");
      const tokendata = {
        token: payPalOrder?.token,
      };
      handlePayPalPayment(tokendata, payPalOrder);
      setTimeout(() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        navigate({ search: "" });
      }, 500);
    }
  }, []);

  const handleApplePay = async (success: boolean, paymentIntent: any) => {
    const { lead_owner, balance, first_name, last_name, accountid } =
      userData?.data?.accountData || {};
    try {
      let payload = {
        lead_id: accountLeadData?.lead_id,
        cluster_id: decode(productId || "")?.split("_")[1],
        balance: balance,
        x_payment_method: "AP",
        x_email: paymentProcess.email,
        city: "",
        address1: "",
        address2: "",
        zipcode: "",
        state: "",
        country: "",
        first_name: first_name,
        last_name: last_name,
        accountid: accountid,
        amount_type: "amount",
        amount: amount,
        use_old_card: !isAddNewCard ? "0" : "1",
        description: "Online Payment",
        payment_note: !isRecurring ? "One-time" : "Subscription",
        customer_device: getOperatingSystem(),
        card_type: "",
        old_cardnumber: "",
        old_cvv: "",
        old_expiry: "",
        old_4digit: "",
        old_type: "",
        old_zip: "",
        cvv: "",
        cexpiry: "",
        ccardnumber: "",
        choldername: "",
        aholdername: "",
        routing_number: "",
        account_number: "",
        bank_name: "",
        bank_address: "",
        cc_token: "",
        stripe_token: "",
        product_link: productId,
        agent: lead_owner,
        lead_owner: lead_owner,
        transaction_id: paymentIntent?.id,
        isUpsellEnabled: productLinkData?.enabled ? "1" : "0",
        custom_conv:
          productLinkData.prodPermission.custom_conv === "Y" ? "on" : "off",
      };
      let res: any = await dispatch(productLinkPay(payload)).unwrap();

      if (res?.body?.success) {
        let { data } = res?.body;
        paymentProcess.setInvoiceNumber(data?.invoice_number || "");
        paymentProcess.setPaymentProcess("true");
        paymentProcess.setInvoiceSlug(data?.invoice_slug);
        paymentProcess.setOrderId(data?.invoice_id);
        paymentProcess.setAmountDisplay(data?.total_amount);
        paymentProcess.setEmail(data?.email);
        paymentProcess.setLeadId(data?.lead_id);
        paymentProcess.setIsScreenLoading(false);
        paymentProcess.setAccountId(
          data?.accountid || userData?.data?.accountData?.accountid
        );
        await handleSendEmail({
          email: data?.email,
          invoice_slug: data?.invoice_slug,
          order_id: data?.invoice_id,
          accountId: data?.accountid || userData?.data?.accountData?.accountid,
        });
      } else {
        paymentProcess.setPaymentProcess("false");
        paymentErrors.setFailedReason(
          res?.body?.message ??
            res?.body?.msg ??
            res?.body?.data?.gateway_error ??
            res?.body?.data?.message ??
            "Payment failed"
        );
        paymentProcess.setIsScreenLoading(false);
      }
    } catch (error) {
      paymentProcess.setPaymentProcess("false");
      paymentErrors.setFailedReason("Payment failed");
      paymentProcess.setIsScreenLoading(false);
      console.log("error =====", error);
    }
  };

  const handleAccept = async () => {
    await dispatch(onSetFinalDisclosureLoading(true));
    try {
      if (isAddNewCard) {
        await handleSubmit();
      } else {
        switch (selectedMethod) {
          case "Card":
            if (
              paymentPermission?.cc_gateway_name?.name ===
                PaymentGateway.STRIPE &&
              cardRef.current
            ) {
              await cardRef.current.callHandleClick();
            } else {
              await handleSubmit();
            }
            break;
          case "Amazon Pay":
            if (amazonRef.current) {
              await amazonRef.current.callHandleClick();
            } else {
              await handleSubmit();
            }
            break;
          case "Bank":
            let check = checkBankManualFormOrPlaid({
              manual_ach: portalSettings?.manual_ach,
              validate_bank_account: portalSettings?.validate_bank_account,
              isManualForm,
            });
            if (
              (paymentPermission?.ach_gateway_name?.name ===
                PaymentGateway.STRIPE ||
                check) &&
              bankRef.current
            ) {
              await bankRef.current.callHandleClick();
            } else {
              await handleSubmit();
            }
            break;
          case "Cash App Pay":
            if (cashAppRef.current) {
              await cashAppRef.current.callHandleClick();
            }
            break;
          case "Wire":
            await handleSubmit();
            break;
          case "Google Pay":
            await handleGooglePay(tokenData?.token, tokenData?.total_amount);
            break;
          case "PayPal":
            window.location.href = payPalOrder.url;
            break;
          default:
            console.log("selectedMethod =====", selectedMethod);
        }
      }
    } catch (error) {
      console.log("error =====", error);
    }
  };

  useEffect(() => {
    if (isAddNewCard) {
      setShowButtons(true);
    } else {
      if (selectedMethod === "Card") {
        if (
          paymentPermission?.cc_gateway_name?.name === PaymentGateway.STRIPE ||
          paymentPermission?.cc_gateway_name?.name === PaymentGateway.STAX
        ) {
          setShowButtons(false);
        } else {
          setShowButtons(true);
        }
      } else if (selectedMethod === "Bank") {
        let check = checkBankManualFormOrPlaid({
          manual_ach: portalSettings?.manual_ach,
          validate_bank_account: portalSettings?.validate_bank_account,
          isManualForm,
        });
        if (
          paymentPermission?.ach_gateway_name?.name === PaymentGateway.STRIPE ||
          paymentPermission?.ach_gateway_name?.name === PaymentGateway.STAX ||
          check
        ) {
          setShowButtons(false);
        } else {
          setShowButtons(true);
        }
      } else if (selectedMethod === "Wire") {
        setShowButtons(true);
      } else if (selectedMethod === "PayPal") {
        setShowButtons(false);
      } else if (selectedMethod === "Cash App Pay") {
        setShowButtons(false);
      } else if (selectedMethod === "Amazon Pay") {
        setShowButtons(false);
      }
    }
  }, [
    selectedMethod,
    isAddNewCard,
    paymentPermission,
    portalSettings,
    isManualForm,
  ]);

  // conv fee
  useEffect(() => {
    const getMethod = () => {
      if (isAddNewCard) {
        let method = "CC";
        if (
          dataPayment?.selectedBank &&
          Object.keys(dataPayment?.selectedBank).length > 0
        ) {
          method = "ACH";
        }
        return method;
      } else {
        return selectedMethod === "Card"
          ? "CC"
          : selectedMethod === "Bank"
            ? "ACH"
            : selectedMethod === "Cash App Pay"
              ? "CAPP"
              : selectedMethod === "Amazon Pay"
                ? "AMZP"
                : selectedMethod === "PayPal"
                  ? "PP"
                  : selectedMethod === "Wire"
                    ? "WT"
                    : undefined;
      }
    };

    const method = getMethod();

    let pm_id = "";

    if (method === "CC" && isAddNewCard) {
      pm_id = dataPayment?.selectedCard?.cc_token;
    } else if (method === "ACH" && isAddNewCard) {
      pm_id = dataPayment?.selectedBank?.bank_token;
    }

    const getConvFee = async () => {
      let productSlug = decode(productId || "")?.split("_");
      try {
        let params = {
          cluster_id: productSlug[1],
          state:
            cardState.values?.x_state !== ""
              ? cardState.values?.x_state
              : userData?.data?.accountData?.state,
          x_method: method,
          phone_number: userData?.data?.accountData?.phone,
          x_amount: amount,
          lead_id: accountLeadData?.lead_id,
          pm_id,
          custom_conv:
            productLinkData.prodPermission.custom_conv === "Y" ? "on" : "off",
        };

        await dispatch(convFeeGet(params)).unwrap();
      } catch (error) {
        console.log("getConvFee error =====", error);
      }
    };
    if (
      amount > 0 &&
      selectedMethod !== "Wire" &&
      selectedMethod !== "PayPal"
    ) {
      getConvFee();
    }
  }, [amount, selectedMethod, isAddNewCard, dataPayment]);

  useEffect(() => {
    if (userData?.data?.accountData?.email) {
      paymentProcess.setEmail(userData?.data?.accountData?.email || "");
    }

    if (savedCardsEmail) {
      paymentProcess.setEmail(savedCardsEmail);
    }
  }, [userData, savedCardsEmail]);

  //drawer
  useEffect(() => {
    const updateMaxHeight = () => {
      if (drawerRef.current && !productLinkData.isLoading) {
        drawerRef.current.style.maxHeight = showDetails
          ? `${drawerRef.current.scrollHeight}px`
          : "0";
      }
    };
    updateMaxHeight();
    window.addEventListener("resize", updateMaxHeight);
    return () => {
      window.removeEventListener("resize", updateMaxHeight);
    };
  }, [showDetails, productLinkData.isLoading]);

  const generalMarkers = getGeneralDetailMarkers({
    conFee,
    paymentAmount: productLinkData.editVal?.toString(),
  });

  const handlePlaidBankPayment = async (
    value: any,
    isCCTokenAvailable: boolean
  ): Promise<void> => {
    try {
      let payload = {
        x_payment_method: "ACH",
        email: value?.email,
        x_card_num: value?.x_card_num?.replaceAll(" ", ""),
        x_card_code: value?.x_card_code,
        x_exp_date: value?.x_exp_date,
        iconType: cardState.iconType,
        routing_number: value?.routing_number,
        account_number: value?.account_number,
        bankName: value?.bankName,
        bank: value?.bank,
        x_city: value?.x_city || userData?.data?.accountData?.city || "",
        x_address:
          value?.x_address || userData?.data?.accountData?.address1 || "",
        address2:
          value?.address2 || userData?.data?.accountData?.address2 || "",
        x_zip: value?.x_zip || userData?.data?.accountData?.postal_code || "",
        x_state: value?.x_state || userData?.data?.accountData?.state || "",
        x_country:
          value?.x_country || userData?.data?.accountData?.country || "",
        choldername: value?.choldername,
        cppid: value?.cppid,
        cc_token: value?.cc_token,
        cc_token2: value?.cc_token2 ?? value?.cc_token,
        stripe_token: value?.cc_token,
        pc_atid: value?.pc_atid,
        use_old_card: isCCTokenAvailable ? "1" : "0",
      };
      await handleCardBankPayment(payload);
    } catch (error) {
      console.log("error =====", error);
    } finally {
      await dispatch(onSetFinalDisclosureLoading(false));
      await dispatch(onSetShowDisclosure(false));
    }
  };

  const showQuantity = useMemo(() => {
    if (
      !productLinkData ||
      !productLinkData.productData ||
      productLinkData.productData.length === 0
    )
      return false;
    return productLinkData.productData?.some((item: any) => item?.quantity > 1);
  }, [productLinkData]);

  useEffect(() => {
    if (userData?.data?.accountData?.accountid) {
      paymentProcess.setAccountId(userData?.data?.accountData?.accountid);
    }
  }, [userData?.data?.accountData?.accountid]);

  const paramsURL = `${window.location.href}?amount=${amount}&email=${paymentProcess.email}&isUpsellEnabled=${productLinkData?.enabled}&custom_conv=${productLinkData.prodPermission.custom_conv}`;

  return (
    <div
      className={` w-full lg:h-full ${productLinkData?.isProdLinkActive ? "lg:grid grid-cols-2 lg:overflow-y-auto overflow-y-scroll" : "flex justify-center items-center h-[var(--shell-vh)]"} `}
      {...testAttrs("page", "Product payment link")}
    >
      {productLinkData?.isProdLinkActive ? (
        <>
          {/* PAYMENT DESCRIPTION PART */}
          <div
            style={{ backgroundColor: brandColor }}
            className="relative lg:h-full lg:max-h-[var(--shell-vh)] lg:overflow-y-scroll lg:py-[90px] py-20 pb-5 md:px-10 px-5"
            {...testAttrs("details-panel", "Product payment details")}
          >
            <div className={` w-full lg:h-full`}>
              <div className="flex justify-center lg:mt-[7%] h-fit lg:pb-16">
                <div
                  className={`flex flex-col items-start lg:gap-10 gap-5 w-full lg:max-w-[380px]`}
                >
                  <div className="lg:flex hidden items-center gap-[10px]">
                    {productLinkData.isLoading ? (
                      <Skeleton width={100} height={35} />
                    ) : (
                      <div className="h-16 lg:h-16 flex justify-end">
                        <img
                          src={paymentLogo}
                          alt="Company logo"
                          className="animate__animated animate__fadeIn object-contain"
                        />
                      </div>
                    )}
                  </div>
                  {/* PAYMENT TYPE AND AMOUNT */}
                  <div className={`flex flex-col items-start w-full`}>
                    <span className="w-full flex justify-between items-start">
                      <div
                        className={`${
                          isDark
                            ? "!text-darktxtParagraph"
                            : "!text-txtParagraph"
                        } font-bold lg:text-xl text-lg leading-[26.04px] lg:w-[95%] w-[70%] break-words`}
                      >
                        {productLinkData.isLoading ? (
                          <Skeleton width={300} height={25} />
                        ) : (
                          productLinkData.title
                        )}
                      </div>
                      <div
                        className={`lg:hidden text-base font-medium underline ${
                          isDark
                            ? "text-darkTxtBlue"
                            : "text-blue hover:!text-btnPrimaryHover"
                        } hover:!text-btnPrimaryHover cursor-pointer`}
                        role="button"
                        tabIndex={0}
                        {...testAttrs(
                          "toggle-details",
                          showDetails
                            ? "Hide product payment details"
                            : "View product payment details"
                        )}
                        onClick={() => setShowDetails(!showDetails)}
                      >
                        {showDetails ? "Hide details" : "View details"}
                      </div>
                    </span>
                    <div className="flex justify-between w-full lg:mt-4 mt-6 relative">
                      <div
                        className={`font-bold lg:leading-[65.1px] leading-[41.66px] ${
                          isDark ? "text-darkTxtPrimary" : "text-txtPrimary"
                        }  lg:text-[50px] text-[32px]`}
                      >
                        {productLinkData.isLoading
                          ? "$0.00"
                          : formatCurrency(productLinkData.editVal) || "$0.00"}
                      </div>
                    </div>
                    {productLinkData.subtitle && !productLinkData.isLoading && (
                      <p
                        className={`${
                          isDark
                            ? "!text-darktxtParagraph"
                            : "!text-txtParagraph"
                        } text-sm  leading-[20px]`}
                      >
                        {productLinkData.subtitle}
                      </p>
                    )}
                  </div>
                  <span
                    ref={drawerRef}
                    className={`drawer1 ${
                      showDetails && "open"
                    } flex flex-col w-full lg:gap-10 gap-5`}
                    {...testAttrs(
                      "payment-details-drawer",
                      "Product payment details"
                    )}
                  >
                    {/* LINE */}
                    <div className="h-[1px] bg-strokeActive lg:max-w-[380px] w-full" />
                    <PaymentDetails
                      amount={amount}
                      productData={productLinkData.productData}
                      promoEdit={promoEdit}
                      addPromo={addPromo}
                      showQuantity={showQuantity}
                      promoRef={promoRef}
                      setAddPromo={setAddPromo}
                      setPromoEdit={setPromoEdit}
                      upsellPriceData={productLinkData.upsellPriceData}
                      upsellEnabled={productLinkData.enabled}
                      setUpsellEnabled={productLinkData.setEnabled}
                      setAmount={setAmount}
                      setEditVal={productLinkData.setEditVal}
                    />
                  </span>
                  <Footer
                    style={{ backgroundColor: brandColor }}
                    className={`!justify-start xl:!w-full !w-[42.5%] lg:max-w-[380px] lg:flex hidden xl:flex-nowrap flex-wrap ${
                      isDark ? "!text-darktxtParagraph" : "!text-txtParagraph"
                    }`}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* PAYMENT METHOD PART */}
          <div
            ref={stepperAnchorRef}
            className="h-0 lg:hidden block"
            style={{ scrollMarginTop: "84px" }}
            {...testAttrs("payment-stepper-anchor")}
          />

          <div
            className={`text-xl font-medium w-full lg:h-full h-fit ${showFinalDisclosure ? "" : "lg:pt-[100px]"} lg:overflow-y-scroll`}
            {...testAttrs("checkout-panel", "Product checkout")}
          >
            {isScreenLoading || productLinkData.isLoading ? (
              <div
                className="w-full h-full flex justify-center items-center"
                {...testAttrs("loading", "Loading product checkout")}
              >
                <div
                  className="h-10 w-10 animate-spin rounded-full border-2 border-solid border-blue border-r-transparent align-[-0.125em] text-primary motion-reduce:animate-[spin_1.5s_linear_infinite]"
                  aria-hidden="true"
                ></div>
              </div>
            ) : !isSubscriptionAllowed ? (
              // ) : true ? (
              <div
                className="h-full w-full flex justify-center"
                {...testAttrs(
                  "subscription-not-allowed",
                  "Subscription not allowed"
                )}
              >
                <NotAllowed
                  email={paymentProcess.email}
                  setEmail={paymentProcess.setEmail}
                  isLoggedIn={isLogin}
                  handleContinue={() => {
                    let productSlug = decode(productId || "")?.split("_");
                    const clusterId = productSlug[1];
                    const clusterSlug = encode(`${clusterId}`, "ci_");

                    if (isLogin) {
                      navigate(`/${clusterSlug}/subscriptions-plans`, {
                        state: { status: "active" },
                      });
                    } else {
                      let state = {
                        status: "active",
                      };
                      dispatch(
                        onSetLastRoute({
                          pathname: `/${clusterSlug}/subscriptions-plans`,
                          state,
                          clusterId: `${clusterId}`,
                        })
                      );
                      navigate(`/${clusterSlug}/login`);
                    }
                  }}
                  handleCheckout={() => {
                    let productSlug = decode(productId || "")?.split("_");
                    const clusterId = productSlug[1];
                    const clusterSlug = encode(`${clusterId}`, "ci_");

                    if (isLogin) {
                      dispatch(
                        onSetLastRoute({
                          pathname: location.pathname,
                          search: location.search,
                          clusterId: `${clusterId}`,
                        })
                      );
                      localStorage.removeItem(TOKEN_STORAGE_KEY);
                      localStorage.removeItem(TOKEN_STORAGE_TIME);
                      clearSlice(dispatch);
                      dispatch(updateTokenStatus(false));
                      dispatch(updateTokenExpiredStatus(false));
                      navigate(`/${clusterSlug}/login`);
                    } else {
                      paymentProcess.setEmail("");
                      dispatch(resetSubscriptionState());
                    }
                  }}
                />
              </div>
            ) : paymentProcess.paymentProcess ? (
              paymentProcess.paymentProcess === "true" ? (
                paymentProcess.isWirePayment ? (
                  <div
                    className="h-full w-full grid place-items-center"
                    {...testAttrs("wire-payment-result", "Wire payment result")}
                  >
                    <WirePaymentSuccess
                      bankDetails={bankState.wireDetails}
                      orderId={paymentProcess.orderId}
                      isInvoice={paymentProcess.invoiceSlug ? true : false}
                      invoiceNumber={paymentProcess.invoiceNumber}
                      invoiceSlug={paymentProcess.invoiceSlug}
                      sendEmail={sendEmail}
                      lead_id={paymentProcess.leadId}
                    />
                  </div>
                ) : (
                  <div
                    className="h-full w-full grid place-items-center"
                    {...testAttrs("payment-result", "Product payment result")}
                  >
                    <ProductLinkSuccess
                      email={paymentProcess.email}
                      amount={paymentProcess.amountDisplay}
                      orderId={paymentProcess.orderId}
                      invoiceSlug={paymentProcess.invoiceSlug}
                      sendEmail={sendEmail}
                      isRecurring={isRecurring}
                      clusterId={decode(productId || "")?.split("_")[1]}
                      lead_id={paymentProcess.leadId}
                      invoiceNumber={paymentProcess.invoiceNumber}
                      accountId={paymentProcess.accountId}
                      showSuccessToast
                    />
                  </div>
                )
              ) : (
                <div {...testAttrs("payment-failed", "Product payment failed")}>
                  <PaymentLinkProcessFailed
                    failedReason={paymentErrors.failedReason}
                    handleBackToStart={handleBackToStart}
                  />
                </div>
              )
            ) : portalSettings?.state_disclosure_flag === SettingsValue.OFF ||
              initialDisclosureAccept ? (
              <div
                className={`${
                  showFinalDisclosure
                    ? "h-full"
                    : "px-5 grid place-items-center mt-2  w-full mb-3"
                } `}
                {...testAttrs("payment-form-section", "Product payment form")}
              >
                <div
                  className={`${
                    showFinalDisclosure ? "hidden none" : "flex"
                  } flex-col lg:max-w-[510px] lg:gap-10 gap-5  text-LightTextParagraph lg:!w-full`}
                >
                  {isAddNewCard ? (
                    <>
                      <div
                        {...testAttrs(
                          "saved-payment-method-section",
                          "Saved payment method"
                        )}
                      >
                        <div className="flex justify-between items-center mb-5">
                          <p className="lg:text-xl text-base font-medium text-txtPrimary">
                            Payment method
                          </p>

                          <p
                            onClick={() => {
                              dispatch(setIsPaymentProcessing(true));
                              setIsAddNewCard(false);
                            }}
                            className="flex gap-2 items-center cursor-pointer xl:text-sm text-xs  font-medium text-blue hover:!text-btnPrimaryHover"
                            role="button"
                            tabIndex={0}
                            {...testAttrs(
                              "add-new-payment-method",
                              "Add new payment method"
                            )}
                          >
                            <AddCircleIconBlue aria-hidden="true" /> Add New
                          </p>
                        </div>

                        <div
                          {...testAttrs(
                            "saved-payment-method-form",
                            "Saved payment method form"
                          )}
                        >
                          <PaymentProcessStep2
                            data={dataPayment}
                            setData={setDataPayment}
                            setCurrentStep={() => {}}
                            isDataLoading={isDataLoading}
                            currentStep={2}
                            walletList={walletList}
                            setSavedCardsEmail={setSavedCardsEmail}
                            isPaymentLink={true}
                            paymentMethodList={paymentMethodList}
                            setSelectedMethod={setSelectedMethod}
                            collectPhone={
                              productLinkData.prodPermission.collect_phone ===
                              "Y"
                            }
                            phoneNumber={productLinkData?.phoneNumber}
                            setPhoneNumber={productLinkData?.setPhoneNumber}
                            handleEmailBlur={handleEmailBlur}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div
                      {...testAttrs(
                        "payment-method-form",
                        "Product payment method form"
                      )}
                    >
                      <PaymentMethodsComponent
                        cardData={{
                          cardNumber: cardState.cardNumber,
                          isDateErr: cardState.isDateErr,
                          isErr: cardState.isErr,
                          cardErrMsg: cardState.cardErrMsg,
                          handleChangeCard,
                          validateCard: () =>
                            validateCard({
                              values: cardState.values,
                              iconType: cardState.iconType,
                              setIsErr: cardState.setIsErr,
                              setCardErrMsg: cardState.setCardErrMsg,
                              setErrMsg: paymentErrors.setErrMsg,
                            }),
                          iconType: cardState.iconType,
                          setIconType: cardState.setIconType,
                          setIsAddNewCardMakePayment: setIsAddNewCard,
                          isAddNewCardMakePayment: isAddNewCard,
                          showNewStripeCardForm: true,
                        }}
                        bankData={{
                          bankName: bankState.bankName,
                          numberNotmatchedError:
                            paymentErrors.numberNotmatchedError,
                          setnumberNotmatchedError:
                            paymentErrors.setnumberNotmatchedError,
                          handleChangeBank,
                          selectedState,
                          setSelectedState,
                          stripeBankURL: `${paramsURL}&method=bank`,
                          isManualForm,
                          setIsManualForm,
                          handlePlaidBankPayment,
                        }}
                        paymentConfig={{
                          paymentMethodList,
                          selectedMethod,
                          setSelectedMethod,
                          amount,
                          isPaymentLink: true,
                          isProductLink: true,
                          custom_conv:
                            productLinkData.prodPermission.custom_conv === "Y"
                              ? "on"
                              : "off",
                          isStaxPayPalLoading,
                          handleEmailBlur,
                        }}
                        paymentHandlers={{
                          handleGooglePay: (tokenData: any, amount: any) => {
                            if (
                              portalSettings?.payment_agreement_opt ===
                                SettingsValue.OFF ||
                              !isLogin
                            ) {
                              handleGooglePay(tokenData, amount);
                            } else {
                              setSelectedMethod("Google Pay");
                              setTokenData({
                                token: tokenData,
                                total_amount: amount,
                              });
                              dispatch(onSetShowDisclosure(true));
                            }
                          },
                          createPayPalIntent,
                          handleApplePay,
                          verifyStripeCardPayment,
                          verifyStaxCardBank,
                          verifyStaxPayPal,
                        }}
                        paymentUrls={{
                          cashAppURL: `${paramsURL}&method=cashapp`,
                          stripeAmazonPayURL: `${paramsURL}&method=amazonPay`,
                        }}
                        formState={{
                          values: cardState.values,
                          email: paymentProcess.email,
                          setEmail: paymentProcess.setEmail,
                          errMsg: paymentErrors.errMsg,
                          isRingLoading,
                        }}
                        paymentGatewayState={{
                          isGoogleLoading,
                        }}
                        componentRefs={{
                          cashAppRef,
                          bankRef,
                          cardRef,
                          amazonRef,
                        }}
                        phoneAndAddressConfig={{
                          collectAddress:
                            productLinkData.prodPermission.collect_address ===
                            "Y",
                          collectPhone:
                            productLinkData.prodPermission.collect_phone ===
                            "Y",
                          phoneNumber: productLinkData?.phoneNumber,
                          setPhoneNumber: productLinkData?.setPhoneNumber,
                        }}
                        noAuthClusterId={decode(productId || "")?.split("_")[1]}
                      />
                    </div>
                  )}

                  <div className="w-full">
                    {showButtons && (
                      <>
                        <Button
                          text={`
                      Pay ${
                        formatCurrency(parseFloat(amount) + conFee) || "$0.00"
                      }
                    `}
                          className={`w-full`}
                          isDisabled={
                            isDisabled ||
                            (selectedMethod === "Bank" &&
                              !isAddNewCard &&
                              (isRingLoading ||
                                paymentErrors.numberNotmatchedError))
                          }
                          isLoading={isBTNLoading}
                          {...testAttrs(
                            "submit-payment",
                            `Pay ${formatCurrency(parseFloat(amount) + conFee) || "$0.00"}`
                          )}
                          onClick={() => {
                            if (
                              portalSettings?.payment_agreement_opt ===
                                SettingsValue.OFF ||
                              !isLogin
                            ) {
                              handleSubmit();
                            } else {
                              dispatch(onSetShowDisclosure(true));
                            }
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>
                {showFinalDisclosure && (
                  <div
                    className={`h-full lg:max-h-[var(--shell-vh)] relative content w-full lg:pt-20 lg:pb-[40px] lg:px-10 md:p-10 p-5 max-w-[740px] mx-auto text-base content ${
                      !showFinalDisclosure ? `hidden none` : ""
                    }`}
                    {...testAttrs(
                      "final-disclosure",
                      "Final product payment disclosure"
                    )}
                  >
                    <DisclosureFinal
                      isOpen={showFinalDisclosure}
                      generalMarkers={generalMarkers}
                      handleAccept={handleAccept}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div
                className="xl:px-20 lg:px-10 px-5 h-full w-full lg:pt-0 pt-5"
                {...testAttrs(
                  "initial-disclosure",
                  "Initial product payment disclosure"
                )}
              >
                <DisclosureInitial
                  isOpen={true}
                  handleAccept={() => setInitialDisclosureAccept(true)}
                  generalMarkers={generalMarkers}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div
            className="h-fit w-fit p-5"
            {...testAttrs("inactive-link", "Product link inactive")}
          >
            <div className="flex flex-col gap-10 justify-center items-center">
              <BigInfoCircle aria-hidden="true" />
              <p className="lg:text-2xl text-lg text-txtPrimary font-medium leading-normal">
                The link is no longer active
              </p>
            </div>
          </div>
          <Footer
            className={` ${
              isSmallSidebar
                ? "lg:!w-[calc(var(--shell-vw)-72px)] lg:left-[72px]"
                : "lg:!w-[calc(var(--shell-vw)-254px)] lg:left-[254px]"
            } lg:px-0 px-5 !text-txtParagraph transition-all duration-300 ease-in-out`}
          />
        </>
      )}
    </div>
  );
};

const ProductImage = ({
  imagePath,
  imageName,
}: {
  imagePath: string;
  imageName: string;
}) => {
  const [isImgErr, setIsImgErr] = useState(false);
  return (
    <>
      {!isImgErr ? (
        <img
          src={`${imagePath}`}
          alt={imageName || "Product image"}
          className="w-[26px] h-[26px] object-contain "
          onError={(e) => {
            setIsImgErr(true);
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

const PaymentDetails = ({
  amount,
  setAmount,
  productData,
  promoEdit,
  addPromo,
  showQuantity,
  promoRef,
  setAddPromo,
  setPromoEdit,
  upsellPriceData,
  upsellEnabled,
  setUpsellEnabled,
  setEditVal,
}: {
  amount: any;
  productData: any[];
  setAmount: React.Dispatch<React.SetStateAction<any>>;
  promoEdit: string;
  addPromo: boolean;
  showQuantity: boolean;
  promoRef: React.RefObject<HTMLDivElement>;
  setAddPromo: React.Dispatch<React.SetStateAction<boolean>>;
  setPromoEdit: React.Dispatch<React.SetStateAction<string>>;
  upsellPriceData: any;
  upsellEnabled: boolean;
  setUpsellEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  setEditVal: (val: number) => void;
}) => {
  const isUpsellEnabled = upsellPriceData && upsellEnabled;
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const getProductRowKey = (item: any) =>
    item?.productDetails?.id ||
    item?.productDetails?.product_id ||
    item?.priceDetails?.id ||
    item?.priceDetails?.price_id ||
    item?.productDetails?.name ||
    "unknown";
  const getItemDisplayData = (item: any) => {
    const hasUpsell =
      Boolean(isUpsellEnabled) && item?.priceDetails?.upsellid > 0;
    const description = hasUpsell
      ? upsellPriceData?.description
      : item?.productDetails?.description;
    const schedule = hasUpsell
      ? upsellPriceData?.schedule
      : item?.priceDetails?.schedule;
    const intervalCount = hasUpsell
      ? upsellPriceData?.interval_custom_count
      : item?.priceDetails?.interval_custom_count;
    const intervalSelector = hasUpsell
      ? upsellPriceData?.interval_custom_selector
      : item?.priceDetails?.interval_custom_selector;
    const price = hasUpsell
      ? upsellPriceData?.price * (item.quantity || 1)
      : item?.priceDetails?.price * (item.quantity || 1);

    return {
      hasUpsell,
      description,
      schedule,
      intervalCount,
      intervalSelector,
      price,
    };
  };

  useEffect(() => {
    let newAmount = amount;
    if (productData && productData?.length > 0 && upsellPriceData) {
      newAmount = productData.reduce((total: number, item: any) => {
        if (item?.priceDetails?.upsellid > 0 && isUpsellEnabled) {
          return total + (upsellPriceData?.price * (item.quantity || 1) || 0);
        } else {
          return (
            total + (item?.priceDetails?.price * (item.quantity || 1) || 0)
          );
        }
      }, 0);
    }
    setAmount(newAmount);
    setEditVal(newAmount);
  }, [isUpsellEnabled, upsellPriceData]);

  return (
    <div
      className="lg:max-w-[380px] w-full"
      {...testAttrs("payment-details-card", "Product payment details")}
    >
      <div
        className="border border-strokePrimary rounded-t-xl px-6 py-5 bg-bgThird flex flex-col gap-1"
        {...testAttrs("payment-details-header")}
      >
        <span className="lg:text-xl text-lg font-bold text-txtPrimary lg:leading-[26.04px] leading-[23.44px]">
          Payment Details
        </span>
        <span className="text-sm leading-[18.23px] text-txtSecondary">
          Review your items below
        </span>
      </div>
      <div className="border-x pt-5 border-strokePrimary flex flex-col bg-bgPrimary gap-4">
        {productData?.map((item: any, index: number) => {
          const displayData = getItemDisplayData(item);
          const productRowKey = getProductRowKey(item);
          return (
            <React.Fragment key={productRowKey}>
              <div
                className="px-5 w-full flex justify-between items-start"
                data-testid={tid(`product-row-${productRowKey}`)}
                aria-label={`${item?.productDetails?.name || "Product"} payment item`}
              >
                <div className="w-[75%] flex gap-2">
                  {item?.productDetails?.image_path && (
                    <ProductImage
                      imagePath={item?.productDetails?.image_path}
                      imageName={item?.productDetails?.image_name}
                    />
                  )}
                  <div className="w-full flex flex-col">
                    <p className="w-full lg:text-[22px] text-lg font-medium leading-[28.64px] text-txtParagraph capitalize">
                      {item?.productDetails?.name.toLowerCase()}
                    </p>
                    {item?.productDetails?.description && (
                      <p className="w-full text-xs leading-[15.62px] text-txtSecondary">
                        {displayData.description}
                      </p>
                    )}
                    {item?.priceDetails?.frequency === "recurring" ? (
                      <p className="w-full text-xs leading-[15.62px] text-txtParagraph">
                        {showQuantity ? `Qty ${item.quantity}, ` : ""}
                        Billed{" "}
                        {formatFrequency(
                          displayData.schedule,
                          displayData.intervalCount,
                          displayData.intervalSelector,
                          true
                        )}
                      </p>
                    ) : (
                      <p className="w-full text-xs leading-[15.62px] text-txtParagraph">
                        {showQuantity ? `Qty ${item.quantity}` : ""}
                      </p>
                    )}
                  </div>
                </div>
                <div className="font-bold text-[22px] text-txtPrimary leading-normal">
                  {formatCurrency(displayData.price) || "$0.00"}
                </div>
              </div>
              {item?.priceDetails?.upsellid > 0 && upsellPriceData ? (
                <UpsellPriceBanner
                  productDetails={item}
                  upsellDetails={upsellPriceData}
                  upsellEnabled={upsellEnabled}
                  setUpsellEnabled={setUpsellEnabled}
                  productQuantity={item.quantity}
                />
              ) : null}
              <div className="w-full px-5">
                <div className="h-[1px] w-full bg-strokePrimary" />
              </div>
            </React.Fragment>
          );
        })}
        <div className="px-5 pb-5 flex flex-col w-full ">
          <div className="w-full flex justify-between items-center">
            <p className="text-sm text-txtPrimary font-bold leading-[18.23px] uppercase">
              Subtotal
            </p>
            <p className="text-sm text-txtPrimary font-bold leading-[18.23px] uppercase">
              {formatCurrency(amount)}
            </p>
          </div>
          <div ref={promoRef}>
            {addPromo ? (
              <div className="w-full  mt-4 ">
                <InputField
                  type="text"
                  name="promo"
                  {...testAttrs("promo-code-input", "Promo code")}
                  label=""
                  placeholder="Add promo code"
                  value={promoEdit}
                  onChange={(e) => setPromoEdit(e?.target?.value)}
                  className="!py-2 !text-sm"
                  promo={true}
                  onClickApply={() => setAddPromo(false)}
                  onKeyDown={(e: any) => {
                    if (e?.code?.toLowerCase() === "enter") {
                      setAddPromo(false);
                    }
                  }}
                />
              </div>
            ) : promoEdit !== "" ? (
              <div className="w-full mt-4  flex justify-between">
                <div className="flex flex-col">
                  <p className="text-sm bg-[#EEF3FC] flex gap-1 py-2 px-[10px] border rounded-md border-strokePrimary justify-center items-center">
                    <Tag />
                    {promoEdit}
                    <span
                      className="cursor-pointer"
                      role="button"
                      tabIndex={0}
                      {...testAttrs("remove-promo-code", "Remove promo code")}
                      onClick={() => setPromoEdit("")}
                    >
                      <CloseIcon aria-hidden="true" />
                    </span>
                  </p>
                  <p className="text-[10px] text-red">Invalid Code</p>
                </div>
                <div className="text-sm pt-2 h-full"></div>
              </div>
            ) : (
              <p
                className="w-fit mt-4 text-TextBlue2 hover:!text-TextBlue2Hover text-sm font-medium cursor-pointer"
                role="button"
                tabIndex={0}
                {...testAttrs("add-promo-code", "Add promo code")}
                onClick={() => setAddPromo(true)}
              >
                Add promo code
              </p>
            )}
          </div>
        </div>
      </div>
      <div
        className="border border-strokePrimary rounded-b-xl px-6 py-5 bg-bgThird flex justify-between items-center"
        {...testAttrs("payment-total", "Product payment total")}
      >
        <p className="text-xl font-bold text-txtPrimary leading-[26.04px] uppercase">
          Total
        </p>
        <p className="text-xl font-bold text-txtPrimary leading-[26.04px] uppercase">
          {formatCurrency(amount)}
        </p>
      </div>
    </div>
  );
};

const UpsellPriceBanner = ({
  productDetails,
  upsellDetails,
  upsellEnabled,
  setUpsellEnabled,
  productQuantity = 1,
}: {
  productDetails: any;
  upsellDetails: any;
  upsellEnabled: boolean;
  setUpsellEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  productQuantity?: number;
}) => {
  const tid = useTid();
  const testAttrs = useTestAttrsFactory();
  const upsellData = useMemo(() => {
    if (!productDetails || !upsellDetails) return null;
    const data = calculateUpsellSavings(
      productDetails.priceDetails,
      upsellDetails,
      productQuantity
    );

    return data;
  }, [productDetails, upsellDetails, productQuantity]);

  return upsellData ? (
    <div
      className="px-5 bg-BannerBlue py-2.5 flex items-center gap-x-2"
      {...testAttrs("upsell-banner", "Subscription billing offer")}
    >
      <ToggleSwitch
        enabled={upsellEnabled}
        setEnabled={setUpsellEnabled}
        size={true}
        showText={false}
        aria-label="Toggle subscription billing offer"
        data-testid={tid("upsell-toggle")}
      />
      <div className="text-sm leading-[15.62px] text-txtPrimary flex items-center gap-x-1">
        <div className="py-1 px-2 bg-BackGreen text-TextGreen">
          Save {formatCurrency(upsellData?.savingsAmount)}{" "}
        </div>
        with {upsellData?.frequency} billing
      </div>
    </div>
  ) : (
    <></>
  );
};

const ProductLink = () => (
  <TestIdScope scope="product-link">
    <ProductLinkContent />
  </TestIdScope>
);

export default ProductLink;
